import { GlobalAddressMode, type GlobalAddress } from "@interfaces/address/globalAddress";
import { MIN_ADDRESS_SEARCH_VALUE_LENGTH } from "@lib/constants/checkout";
import { useOutsideClick } from "@ecco/ui/use-outside-click";
import { debounce } from "lodash-es";
import type React from "react";
import { useMemo, useRef, useState } from "react";
import { useExtraConfig } from "./useExtraConfig";
import { getMarketSearchField, getStates } from "@components/commerce/baseForms/helpers/helpers";
import { getUniqueAddressAsync, useAddressSearch } from "@store/hooks/useAddressSearch";
import { activeSiteConfig } from "@config/site/site-config";
import { Market } from "@config/site/types";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { useFormikContext } from "formik";
import type { TCountryCode } from "countries-list";
import type { CheckoutFormValues } from "@features/checkout/components/delivery-step/types";

export type AddressLookupFieldValues = {
    postalCode?: string;
    streetName?: string;
    city?: string;
    region?: string;
    district?: string;
};

const DEBOUNCE_TIME = 500;

export type UseAddressLookupParams = {
    id: string;
    prefix: string;
    country: TCountryCode;
    locale?: string;
    onAddressSelect?: (address: AddressLookupFieldValues) => void;
};

export const useAddressLookup = ({
    id,
    prefix,
    country,
    locale,
    onAddressSelect,
}: UseAddressLookupParams) => {
    const { setFieldValue, setFieldTouched } = useFormikContext<CheckoutFormValues>();

    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    // Set when the shopper opens a grouped CA Post row; lists the addresses inside it.
    const [searchRef, setSearchRef] = useState<string>();
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const dropdownRef = useRef(null);

    const minAddressLookupLength =
        activeSiteConfig.checkout.minAddressLookupLength ?? MIN_ADDRESS_SEARCH_VALUE_LENGTH;

    const searchValueKey = getMarketSearchField(country);
    const {
        data: addressSearchData,
        isPending: isLoading,
        error: addressSearchError,
    } = useAddressSearch({
        searchValue: { [searchValueKey]: searchQuery },
        country,
        enabled: searchQuery.length >= minAddressLookupLength,
        staleTime: 500,
        mode: GlobalAddressMode.LIST,
        locale,
        searchRef,
    });

    const addresses = useMemo(() => {
        if (addressSearchError) {
            return [];
        }
        if (!addressSearchData) {
            return [];
        }

        if (searchQuery.length < minAddressLookupLength) {
            return [];
        }
        const { address, addresses } = addressSearchData ?? {};

        if (address && address.extra?.possibleMatches !== 0) {
            return [address];
        }

        return addresses ?? [];
    }, [addressSearchData, addressSearchError, searchQuery, minAddressLookupLength]);

    const { modifyFullAddress } = useExtraConfig({ country });

    const searchAndSetAddresses = useMemo(
        () =>
            debounce((searchValue: string) => {
                setSearchQuery(searchValue);
                setSearchRef(undefined);
                setIsOpen(searchValue.length >= minAddressLookupLength);
            }, DEBOUNCE_TIME),
        [minAddressLookupLength]
    );

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue(event.target.name, event.target.value, false);
        // Browser autofill fires a plain Event, typing and pasting an InputEvent. Opened on an
        // autofill, the suggestions cover the fields below while the shopper is elsewhere.
        const isAutofill =
            event.nativeEvent?.type === "input" && !(event.nativeEvent instanceof InputEvent);
        if (isAutofill) {
            // A search still pending from typing would read the filled value and open anyway.
            searchAndSetAddresses.cancel();
            setSearchQuery("");
            setSearchRef(undefined);
            setIsOpen(false);
            return;
        }
        searchAndSetAddresses(event.target.value);
    };

    const toggleTooltip = () => setIsTooltipOpen(!isTooltipOpen);
    const closeTooltip = () => setIsTooltipOpen(false);
    const openTooltip = () => setIsTooltipOpen(true);

    const getRegionValue = (region: string) => {
        const states = getStates(country, locale);
        const searchRegion = states.find(({ name, englishName, alias, value }) =>
            [name, englishName, alias, value].find((val) => region?.includes(val))
        );
        if (!searchRegion) return;
        return searchRegion.value;
    };

    const getFullAddress = ({ address1, city, region, postalCode }: GlobalAddress) => {
        return [address1, city, region, postalCode].filter(Boolean).join(", ");
    };

    const onClickAddress = async (address: GlobalAddress) => {
        searchAndSetAddresses.cancel();

        // The backend needs the original query alongside the searchRef, so keep searchQuery.
        if (address.searchRef) {
            setSearchRef(address.searchRef);
            return;
        }

        let fullAddressDetails = address;

        if (address.id) {
            try {
                const detailedAddress = await getUniqueAddressAsync({
                    country,
                    addressId: address.id,
                    mode: GlobalAddressMode.LIST,
                    locale,
                });

                fullAddressDetails =
                    detailedAddress?.addresses?.length > 0 ? detailedAddress.addresses[0] : address;
            } catch (error) {
                Logger.error(ServiceType.WEB, "Address Lookup: Error fetching address details", {
                    tag: LogTag.ADDRESS_LOOKUP,
                    error,
                });
            }
        }

        // Check if the postal code is missing.
        if (!fullAddressDetails.postalCode) {
            Logger.info(ServiceType.WEB, "Address Lookup: Missing postal code", {
                tag: LogTag.ADDRESS_LOOKUP,
                postalCode: fullAddressDetails.postalCode,
                addressId: address.id,
            });
        }

        const { address1, postalCode, city, extra, region, district } = fullAddressDetails;

        const fields: AddressLookupFieldValues = {
            postalCode: postalCode,
            streetName: modifyFullAddress(fullAddressDetails) ?? address1,
            city: city,
            region: getRegionValue(extra?.englishAddress ?? region),
            district: district || (country?.toLowerCase() === Market.NZ ? city : undefined),
        };

        const changedItems = Object.entries(fields).filter(([, value]) => {
            return Boolean(!!value && value !== "");
        });

        changedItems.map(([key, value]) => {
            setFieldValue(prefix ? `${prefix}.${key}` : key, value, false);
        });

        setTimeout(() => {
            changedItems.map(([key]) => {
                setFieldTouched(prefix ? `${prefix}.${key}` : key, true, true);
            });
        }, 300);

        onAddressSelect?.(fields);

        setIsOpen(false);
    };

    useOutsideClick({
        ref: dropdownRef,
        enabled: isOpen,
        handler: (event) => {
            event.stopPropagation();
            if (
                event.target !== dropdownRef.current ||
                (event.target as HTMLInputElement)?.id !== id
            )
                setIsOpen(false);
        },
    });

    return {
        dropdownRef,
        onClickAddress,
        onChange,
        addresses,
        isTooltipOpen,
        toggleTooltip,
        closeTooltip,
        openTooltip,
        isLoading,
        hasError: addressSearchError,
        isOpen,
        getFullAddress,
    };
};
